import React, { Fragment, useEffect, useState } from "react";
import CommonCard from "../../common/components/CommonCard";
import { Box, Divider, Grid, Typography } from "@mui/material";
import theme from "../../theme";
import CommonButton from "../../common/components/CommonButton";
import moment from "moment";
import { ServiceOffer, ServiceTypes, TasksPriority, TasksPriorityArr, TaskStatus, TaskStatusDispaly, UserTypeId } from "../../common/const";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { setLoading } from "../../features/authSlice";
import { Add, Close } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import CommonDialog from "../../common/components/CommonDialog";
import CommonFormField from "../../common/components/CommonFormField";
import CommonSelect from "../../common/components/CommonSelect";
import { CreateTask, getTasks, UpdateTask } from "../../api/satactApi";

const Tasks = ({
    fromMentor,
    label,
    options,
    isSkill,
    isProjectMentor,
    isCounselling,
    studentId,
    currentService,
    tutoringType,
    subService,
    studentData
}) => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const [taskOpen, setTaskOpen] = useState(false);
    const [errors, setErrors] = useState({});
    const [tasks, setTasks] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        due_date: '',
        priority: '',
        notes: '',
        resources: '',
        context: '',
        platFormLink: ''
    });

    const getTaskData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const serviceType = (() => {
                // If subService is true, use mentoring/coaching based on current service
                if (subService) {
                    return currentService === ServiceOffer.mentor
                        ? 'PROJECT_MENTORING'
                        : 'SKILL_COACHING';
                }

                // If tutoringType exists, use that mapped value
                if (tutoringType) {
                    return ServiceTypes[tutoringType];
                }

                // Otherwise map from current service
                const serviceMap = {
                    [ServiceOffer.skillCoach]: 'SKILL_COACHING',
                    [ServiceOffer.counsellor]: 'COUNSELLING',
                    [ServiceOffer.mentor]: 'PROJECT_MENTORING'
                };

                return serviceMap[currentService] || currentService;
            })();

            const studentIdentifier = user?.student_id || studentId;

            const response = await getTasks(serviceType, studentIdentifier, subService);
            if (currentService === ServiceOffer.mentor) {
                setTasks(response.data.filter((a) => isProjectMentor ? (a.created_by === user?.user_id) : (a.created_by !== studentData?.user_id)));
            } else {
                setTasks(response.data);
            }
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        if (user?.student_id || studentId) {
            getTaskData();
        }
    }, [currentService, user, fromMentor, tutoringType, subService, studentData]);

    const validateField = (name, value) => {
        switch (name) {
            case 'name':
                if (!value) return 'Title is required';
                return '';
            case 'due_date':
                if (!value) return 'Due Date is required';
                return '';
            case 'priority':
                if (!value) return 'Priority is required';
                return '';
            default:
                return '';
        }
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData).forEach(key => {
            const error = validateField(key, formData[key]);
            if (error) newErrors[key] = error;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value, files } = e.target;
        if (files && files.length > 0) {
            const file = files;

            // Validate file size immediately
            const error = validateField(name, '', file); // Pass empty string for value, file for validation

            setFormData((prev) => ({
                ...prev,
                [name]: file,
            }));

            // Set or clear error
            if (error) {
                setErrors(prev => ({ ...prev, [name]: error }));
            } else {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            }
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
        if (errors[name]) {
            const error = validateField(name, value);
            if (!error) {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            } else {
                setErrors(prev => ({ ...prev, [name]: error }));
            }
        }
    };

    const handleSubmit = async () => {
        try {
            if (!validateForm()) return;
            dispatch(setLoading({
                loading: true
            }));
            const data = new FormData();
            data.append('title', formData.name);
            data.append('description', formData.notes);
            data.append('priority', formData.priority);
            data.append('due_date', new Date(formData.due_date).toISOString());
            data.append('created_by', user?.user_id);
            data.append('assigned_by', user?.user_id);
            data.append('student_id', studentId);
            data.append('status', TaskStatus.PENDING);
            data.append('type', currentService === ServiceOffer.skillCoach ? 'SKILL_COACHING' : currentService === ServiceOffer.counsellor ? 'COUNSELLING' : currentService === ServiceOffer.mentor ? 'PROJECT_MENTORING' : currentService);
            data.append('mentor_profile_id', user?.mentor?.mentor_profile_id);
            data.append('service_offers', currentService);
            data.append('subject_context', formData.context);
            data.append('platform_link', formData.platFormLink);
            if (currentService === ServiceOffer.mentor) {
                data.append('milestone_id', formData.context);
                data.append('project_id', subService);
            }
            if (currentService === ServiceOffer.skillCoach) {
                data.append('student_subject_id', formData.context);
            }
            if (formData.resources) {
                data.append('resource', formData.resources[0]);
            }
            const response = await CreateTask(data);
            if (response.success) {
                showNotification(response.message, 'success');
                setFormData({
                    name: '',
                    due_date: '',
                    priority: '',
                    notes: '',
                    resources: '',
                    context: '',
                    platFormLink: ''
                });
                setTaskOpen(false);
                getTaskData();
                dispatch(setLoading({
                    loading: false
                }));
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    return (
        <Fragment>
            <CommonDialog
                open={taskOpen}
                size="xs"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>Create New Task</Typography>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setTaskOpen(false);
                                    setErrors({});
                                    setFormData({
                                        name: '',
                                        due_date: '',
                                        priority: '',
                                        notes: '',
                                        resources: '',
                                        context: '',
                                        platFormLink: ''
                                    });
                                }}
                            />
                        </Box>
                        <Box width="80%" mb={0.5}>
                            <Typography sx={{ fontSize: 12 }} color={theme.palette.background.dark}>Enter the task details</Typography>
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Grid container spacing={2} mt={0.5} mb={1}>
                        <Grid item md={12} xs={12}>
                            <CommonFormField
                                label="Task Title"
                                type="email"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                error={errors.name}
                                placeholder="Enter Task Title"
                            />
                        </Grid>
                        <Grid item md={12} xs={12}>
                            <CommonSelect
                                label={isProjectMentor ? 'Linked Milestone' : isSkill ? "Tech Context" : isCounselling ? "Category " : "Subject Context"}
                                name="context"
                                value={formData.context}
                                onChange={handleInputChange}
                                placeholder={isProjectMentor ? 'Select Milestone' : isSkill ? "Select Context" : isCounselling ? "Select Category" : "Select Subject"}
                                options={options?.length > 0 ? options : []}
                            />
                        </Grid>
                        {isSkill && (
                            <Grid item md={12} xs={12}>
                                <CommonFormField
                                    label="Platform/Link"
                                    type="email"
                                    name="platFormLink"
                                    value={formData.platFormLink}
                                    onChange={handleInputChange}
                                    placeholder="e.g., LeetCode, GitHub Repo, HackerRank"
                                />
                            </Grid>
                        )}
                        <Grid item md={12} xs={12}>
                            <CommonSelect
                                label="Priority"
                                name="priority"
                                value={formData.priority}
                                onChange={handleInputChange}
                                placeholder="Select Priority"
                                error={errors.priority}
                                options={TasksPriorityArr}
                            />
                        </Grid>
                        <Grid item md={12} xs={12}>
                            <CommonFormField
                                label="Due Date"
                                type="date"
                                name="due_date"
                                value={formData.due_date}
                                onChange={handleInputChange}
                                error={errors.due_date}
                                placeholder="Select Due Date"
                            />
                        </Grid>
                        <Grid item md={12} xs={12}>
                            <CommonFormField
                                label="Description"
                                type="email"
                                name="notes"
                                value={formData.notes}
                                onChange={handleInputChange}
                                placeholder="Describe what success"
                                multiline
                            />
                        </Grid>
                        {!isCounselling && (
                            <>
                                <Grid item md={12} xs={12}>
                                    <CommonFormField
                                        label="Link Resource"
                                        type="file"
                                        name="resources"
                                        value={formData.resources?.[0]?.name}
                                        onChange={handleInputChange}
                                        placeholder="PDF"
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                }
                actionButtons={(
                    <CommonButton
                        label="Create"
                        size="small"
                        background={theme.palette.secondary.main}
                        color={theme.palette.background.main}
                        fontWeight={700}
                        marginBottom="10px"
                        handleClick={() => {
                            handleSubmit();
                        }}
                    />
                )}
            />
            <CommonCard
                borderRadius={2}
                border={`1px solid #0000001A`}
                headerContent={
                    <Fragment>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ padding: '13px' }}
                        >
                            <Box>
                                <Typography fontWeight={600}>{label || 'Assigned Task'}</Typography>
                                {!fromMentor && (
                                    <Typography fontSize={14} color={theme.palette.background.dark}>Track and complete your upcoming assignments.</Typography>
                                )}
                            </Box>
                            {label && (
                                <CommonButton
                                    label="Assign Task"
                                    height="35px"
                                    width="140px"
                                    marginBottom="0px"
                                    marginTop="0px"
                                    startIcon={<Add />}
                                    handleClick={() => setTaskOpen(true)}
                                />
                            )}
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Box
                        sx={{
                            height: !fromMentor ? tasks.length > 0 ? "63vh" : "7vh" : tasks.length > 0 ? "30vh" : '7vh',
                            overflow: "auto"
                        }}
                    >
                        {tasks && tasks.length > 0 ? (
                            tasks.map((task, index) => (
                                <Box
                                    key={index}
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    p="16px"
                                    borderRadius={2}
                                    mb={1}
                                    sx={{
                                        background: theme.palette.background.main
                                    }}
                                >
                                    {/* Task Info */}
                                    {isCounselling ? (
                                        <Box>
                                            <Box
                                                display="flex"
                                                gap={1}
                                                alignItems="center"
                                            >
                                                <img src="/Images/actionplan.png" alt="Action Plan" width="40px" height="auto" />
                                                <Box>
                                                    <Typography fontSize={16} fontWeight="600">{task?.title}</Typography>
                                                    <Typography fontSize={14} color="background.dark">Due Date: {moment(task?.due_date).format('MMM YY')}</Typography>
                                                    <Typography fontSize={14} color="background.dark">Assigned by: <span style={{ color: theme.palette.secondary.main, fontWeight: 600 }}>{`${task?.assigne?.first_name} ${task?.assigne?.last_name}`}</span></Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Box>
                                            <Box
                                                sx={{
                                                    background:
                                                        task.priority === "MEDIUM"
                                                            ? theme.palette.secondary.main
                                                            : task.priority === "HIGH"
                                                                ? 'red'
                                                                : '#01875F',
                                                    px: "5px",
                                                    py: "3px",
                                                    width: "80px",
                                                    textAlign: "center",
                                                    mb: 0.5,
                                                    borderRadius: 1
                                                }}
                                            >
                                                <Typography
                                                    fontSize={13}
                                                    color={
                                                        task.priority === "MEDIUM"
                                                            ? theme.palette.primary.main
                                                            : task.priority === "HIGH"
                                                                ? "white"
                                                                : "white"
                                                    }
                                                >
                                                    {TasksPriority[task.priority]}
                                                </Typography>
                                            </Box>

                                            {/* Title */}
                                            <Typography fontSize={15} fontWeight="600">
                                                {task.title}
                                            </Typography>

                                            {/* Description */}
                                            <Typography
                                                fontSize={13}
                                                color={theme.palette.background.dark}
                                            >
                                                {task.description}
                                            </Typography>

                                            {/* Due Date */}
                                            <Typography
                                                fontSize={14}
                                                color={theme.palette.background.dark}
                                                sx={{ whiteSpace: "nowrap" }}
                                            >
                                                Due {moment(task.due_date).fromNow()}
                                            </Typography>
                                        </Box>
                                    )}
                                    {!fromMentor && user?.user_type_id === UserTypeId.Student && (task?.status === TaskStatus.PENDING || task?.status === TaskStatus.IN_PROGRESS) ? (
                                        <CommonButton
                                            label={task?.status === TaskStatus.PENDING ? "Start" : "Complete"}
                                            fontWeight={600}
                                            width={task?.status === TaskStatus.PENDING ? "50px" : "90px"}
                                            height="30px"
                                            marginBottom="0px"
                                            handleClick={async () => {
                                                try {
                                                    dispatch(setLoading({
                                                        loading: true
                                                    }));
                                                    const response = await UpdateTask(task.task_id, {
                                                        "status": task?.status === TaskStatus?.PENDING ? TaskStatus.IN_PROGRESS : TaskStatus.COMPLETED,
                                                        "completed_at": new Date().toISOString()
                                                    });
                                                    if (response?.success) {
                                                        showNotification(response.message, 'success');
                                                        getTaskData();
                                                    }
                                                } catch (err) {
                                                    const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                                    showNotification(errorMessage, 'error');
                                                    dispatch(setLoading({
                                                        loading: false
                                                    }));
                                                }
                                            }}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                background: '#01875F',
                                                padding: '3px 10px',
                                                borderRadius: 1,
                                            }}
                                        >
                                            <Typography fontSize={13} color={'white'} textAlign="center">{TaskStatusDispaly[task?.status]}</Typography>
                                        </Box>
                                    )}
                                </Box>
                            ))
                        ) : (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    my: 2
                                }}
                            >
                                <Typography color={theme.palette.background.dark}>
                                    No Task Found
                                </Typography>
                            </Box>
                        )}
                    </Box>
                }
                paddingBottom="0px"
                padding="13px"
                marginTop={isProjectMentor || fromMentor ? "0px" : "15px"}
            />
        </Fragment >
    )
}

export default Tasks;